import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Play, Clock, X, ChevronRight, CheckCircle } from 'lucide-react';
import { useTaskTimer } from './TaskTimerContext';

const GlobalTaskMonitorContext = createContext(null);

export const useGlobalTaskMonitor = () => {
  const context = useContext(GlobalTaskMonitorContext);
  if (!context) {
    throw new Error('useGlobalTaskMonitor must be used within GlobalTaskMonitorProvider');
  }
  return context;
};

// Check if a task's start time has arrived today
const isTaskDueToStart = (task) => {
  if (!task.due_time || task.status === 'completed') return false;
  
  const now = new Date();
  const [hours, minutes] = task.due_time.split(':').map(Number);
  const dueDate = new Date();
  dueDate.setHours(hours, minutes, 0, 0);
  
  // Within 1 minute of start time
  const diffMs = now - dueDate;
  return diffMs >= 0 && diffMs < 60000;
};

// Format time to 12-hour format
const formatTime12 = (time) => {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

export const GlobalTaskMonitorProvider = ({ children }) => {
  const { startTimer, runningTaskIds } = useTaskTimer();
  const [tasks, setTasks] = useState([]);
  const [notification, setNotification] = useState(null);
  const [notifiedTasks, setNotifiedTasks] = useState(new Set());
  // Initialize notificationsEnabled from localStorage (default to true if not set)
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const stored = localStorage.getItem('taskNotificationsEnabled');
    return stored !== null ? JSON.parse(stored) : true;
  });
  const checkIntervalRef = useRef(null);
  const autoStartedRef = useRef(new Set());

  // Get userId from localStorage
  const getUserId = useCallback(() => {
    try {
      const authData = localStorage.getItem('auth_user');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user_id || parsed.id;
      }
    } catch (e) { /* ignored */ }
    return null;
  }, []);

  // Fetch notification preferences
  const fetchNotificationPrefs = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    
    try {
      const response = await fetch(`${window.location.origin}/api/user-addons/preferences?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        const prefs = data.preferences || {};
        if (prefs.task_notifications_enabled !== undefined) {
          setNotificationsEnabled(prefs.task_notifications_enabled);
          // Dismiss any existing notification if notifications are now disabled
          if (!prefs.task_notifications_enabled) {
            setNotification(null);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  }, [getUserId]);

  // Fetch tasks from backend
  const fetchTasks = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    
    try {
      const response = await fetch(`${window.location.origin}/api/tasks/?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        const taskList = Array.isArray(data) ? data : (data.tasks || []);
        setTasks(taskList.filter(t => t.status !== 'completed'));
      }
    } catch (error) {
      console.error('Error fetching tasks for global monitor:', error);
    }
  }, [getUserId]);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 600;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      // Second tone
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.value = 800;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.2, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.3);
      }, 150);
    } catch (e) { /* ignore */ }
  }, []);

  // Check for tasks that are due and show notification (no auto-start)
  const checkTasksToStart = useCallback(() => {
    // Skip if notifications are disabled
    if (!notificationsEnabled) {
      return;
    }
    
    tasks.forEach(task => {
      const taskId = task.id || task._id;
      
      // Skip if already running or already notified
      if (runningTaskIds.has(taskId) || autoStartedRef.current.has(taskId)) return;
      
      // Check if task is due to start
      if (isTaskDueToStart(task)) {
        // Mark as notified so we don't re-notify
        autoStartedRef.current.add(taskId);
        
        // Show notification only — no auto-start
        if (!notifiedTasks.has(taskId)) {
          setNotifiedTasks(prev => new Set([...prev, taskId]));
          
          const sortedTasks = [...tasks]
            .filter(t => t.due_time && t.status !== 'completed' && (t.id || t._id) !== taskId)
            .sort((a, b) => a.due_time.localeCompare(b.due_time));
          const nextTask = sortedTasks.find(t => t.due_time > task.due_time);
          
          setNotification({
            type: 'due',
            task,
            nextTask,
            timestamp: new Date()
          });
          
          playNotificationSound();
          
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Task Due!', {
              body: `${task.title} is ready to start${nextTask ? `\nUp next: ${nextTask.title}` : ''}`,
              icon: '/favicon.ico',
              tag: `task-due-${taskId}`
            });
          }
          
          // Auto-dismiss after 15 seconds
          setTimeout(() => {
            setNotification(prev => 
              prev?.task?.id === taskId ? null : prev
            );
          }, 15000);
        }
      }
    });
  }, [tasks, runningTaskIds, notifiedTasks, playNotificationSound, notificationsEnabled]);

  // Dismiss notification
  const dismissNotification = useCallback(() => {
    setNotification(null);
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Initial fetch and set up polling
  useEffect(() => {
    fetchTasks();
    fetchNotificationPrefs();
    
    // Poll for tasks every 30 seconds
    const fetchInterval = setInterval(fetchTasks, 30000);
    
    // Poll for notification preferences every 30 seconds (to catch changes from Tasks page)
    const prefsInterval = setInterval(fetchNotificationPrefs, 30000);
    
    // Listen for localStorage changes for immediate sync
    const handleStorageChange = (e) => {
      if (e.key === 'taskNotificationsEnabled') {
        const newValue = JSON.parse(e.newValue);
        setNotificationsEnabled(newValue);
        if (!newValue) {
          setNotification(null);
        }
      }
    };
    
    // Also check localStorage on interval for same-tab changes
    const localStorageCheck = setInterval(() => {
      const stored = localStorage.getItem('taskNotificationsEnabled');
      if (stored !== null) {
        const parsedValue = JSON.parse(stored);
        setNotificationsEnabled(prev => {
          if (prev !== parsedValue) {
            if (!parsedValue) {
              setNotification(null);
            }
            return parsedValue;
          }
          return prev;
        });
      }
    }, 1000);
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(fetchInterval);
      clearInterval(prefsInterval);
      clearInterval(localStorageCheck);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchTasks, fetchNotificationPrefs]);

  // Check tasks every 10 seconds
  useEffect(() => {
    checkTasksToStart();
    checkIntervalRef.current = setInterval(checkTasksToStart, 10000);
    
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkTasksToStart]);

  // Reset auto-started tasks at midnight
  useEffect(() => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const msUntilMidnight = midnight - now;
    
    const resetTimeout = setTimeout(() => {
      autoStartedRef.current.clear();
      setNotifiedTasks(new Set());
    }, msUntilMidnight);
    
    return () => clearTimeout(resetTimeout);
  }, []);

  return (
    <GlobalTaskMonitorContext.Provider value={{ 
      tasks, 
      notification, 
      dismissNotification,
      fetchTasks,
      notificationsEnabled,
      fetchNotificationPrefs
    }}>
      {children}
      
      {/* Global Task Due Notification Banner - Only show if notifications are enabled */}
      {notificationsEnabled && notification && notification.type === 'due' && (
        <div className="fixed top-0 left-0 right-0 z-[999998] pointer-events-auto animate-slide-down">
          <div className="bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 shadow-2xl">
            <div className="p-4">
              <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 rounded-full">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Bell className="w-5 h-5" />
                      Task Due!
                    </h3>
                    <p className="text-white/90 text-lg font-medium">
                      {notification.task.title || notification.task.name}
                    </p>
                    {notification.task.due_time && (
                      <p className="text-white/70 text-sm flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Scheduled for {formatTime12(notification.task.due_time)}
                        {(notification.task.estimated_hours > 0 || notification.task.estimated_minutes > 0) && (
                          <span className="ml-2">• {notification.task.estimated_hours > 0 ? `${notification.task.estimated_hours}h ` : ''}{notification.task.estimated_minutes > 0 ? `${notification.task.estimated_minutes}m` : ''}</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      const taskId = notification.task.id || notification.task._id;
                      const estimatedSeconds = ((notification.task.estimated_hours || 0) * 3600) + ((notification.task.estimated_minutes || 0) * 60);
                      startTimer(taskId, notification.task, estimatedSeconds);
                      dismissNotification();
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                    data-testid="start-due-task-btn"
                  >
                    <Play size={18} className="fill-white" />
                    Start Timer
                  </button>
                  <a
                    href="/tasks-re"
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors"
                  >
                    <CheckCircle size={18} />
                    View Tasks
                  </a>
                  <button
                    onClick={dismissNotification}
                    className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                    data-testid="dismiss-task-start-notification"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Next task strip */}
            {notification.nextTask && (
              <div className="bg-black/30 px-4 py-2 border-t border-white/10">
                <div className="max-w-4xl mx-auto flex items-center gap-2 text-sm">
                  <ChevronRight size={16} className="text-green-200 flex-shrink-0" />
                  <span className="text-white/70">Up next:</span>
                  <span className="font-semibold text-white">
                    {notification.nextTask.title || notification.nextTask.name}
                  </span>
                  {notification.nextTask.due_time && (
                    <span className="text-green-200 font-medium ml-1">
                      at {formatTime12(notification.nextTask.due_time)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes slide-down {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </GlobalTaskMonitorContext.Provider>
  );
};

export default GlobalTaskMonitorProvider;
